import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ProgressBar, Modal } from 'react-bootstrap';
import './ActionSection.css';
import '../../sharingOptions/sharingOptions.css';
import API from '../../../utils/API';
import { FaCheck, FaRegCopy } from "react-icons/fa";

import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  WhatsappIcon
} from 'react-share';
import twitterX from '../../../images/HomeNewImage/twitter-x-logo.png';
import ReactTooltip from 'react-tooltip';
import { useHistory } from 'react-router-dom';

interface ActionButton {
  id: string;
  title: string;
  icon: string;
  onClick: () => void;
  color?: string; // 'orange' | 'green' | 'purple' | 'blue' | 'gray' | 'link'
}

interface ProgressItem {
  id: string;
  title: string;
  description?: string;
  progress: number; // 0..100
  goal: number;
  raised: number;
}

interface ShareItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'tile' | 'fundraiser';
  accent?: 'orange' | 'pink' | 'green' | 'purple';
}

interface OverviewData {
  yearDonations: number;
  supporters: number;
  allDonations: number;
}

interface ActionSectionProps {
  keyActions: ActionButton[];
  fundraiserProgress: ProgressItem[];
  socialShareItems: ShareItem[];
  overview?: OverviewData;
}

const ActionSection: React.FC<ActionSectionProps> = ({
  keyActions,
  fundraiserProgress,
  socialShareItems,
  overview
}) => {
  const reduxOrg = useSelector((state: any) => state.org);
  const [campaigns, setCampaigns] = useState<any>([]);
  const [overviewData, setOverviewData] = useState<any>({ yearDonations: 0, supporters: 0, allDonations: 0 });
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [selectedSharingItem, setSelectedSharingItem] = useState<string | null>(null);
  const [selectedCampaign, setSelectedselectedCampaign] = useState<any>(null);
  const [copy, setCopy] = useState<any>(false);
  const history = useHistory()

  useEffect(() => {
    API.getNpCampaigns(reduxOrg._id).then((result) => {
      console.log(result.data, 'campaigns');
      setCampaigns(
        result.data.sort((a: any, b: any) => b.createdAt - a.createdAt)
      );
    });

    API.getNpOverview(reduxOrg._id).then((result) => {
      console.log(result.data, 'overview');
      setOverviewData(result.data);
    });
  }, []);

  const fmt$ = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  const fmtN = (n: number) => new Intl.NumberFormat('en-US').format(n);

  const getSharingProps = (id: string) => {
    if (id === 'profile') {
      return {
        componentHeaderText: 'Share your profile page',
        url: 'https://neddie.co/nonprofit/' + reduxOrg._id,
        postDescription: 'Check out the ' + reduxOrg.name + ' profile page on Neddie',
        twitterHashtags: ['Neddie', 'Nonprofits']
      };
    } else if (id === 'wishlist') {
      return {
        componentHeaderText: 'Share your wishlist',
        url: reduxOrg?.actionItems?.wishlist,
        postDescription: 'Check out the ' + reduxOrg.name + ' Wishlist ',
        twitterHashtags: ['Neddie', 'Nonprofits']
      };
    } else if (id === 'store') {
      return {
        componentHeaderText: 'Share your marketplace',
        url: reduxOrg?.actionItems?.marketplace,
        postDescription: 'Check out the ' + reduxOrg.name + ' Marketplace ',
        twitterHashtags: ['Neddie', 'Nonprofits']
      };
    } else if (id === 'fundraiser') {
      return {
        componentHeaderText: 'Share this Fundraiser',
        url: 'https://neddie.co/donation/' + reduxOrg._id + '/' + selectedCampaign.campaignId,
        postDescription: 'Check out the ' + selectedCampaign?.name + ' Fundraiser',
        twitterHashtags: ['Neddie', 'Nonprofits']
      };
    }
    return null;
  };

  const goToProfile = () => {
    history.push(`/nonprofit/${reduxOrg._id}?sec=fundraser`);
  };


  const png = (src: string, alt: string) => (
    <img src={src} alt={alt} className="nav-img" loading="lazy" />
  );
  return (
    <div className="action-section grid-4">

      {/* Overview */}
      <div className="overview-card">
        <div className="overview-header"><h3>Overview</h3></div>
        <ul className="overview-list">
          <li className="overview-row">
            <span className="overview-icon">{png('/icons/15.png', 'heart')}</span>
            <span className="overview-label">Year Donations</span>
            <span className="overview-value">{fmt$(overviewData.yearDonations)}</span>
          </li>
          <li className="overview-row">
            <span className="overview-icon">{png('/icons/16.png', 'Supporters')}</span>
            <span className="overview-label">Supporters</span>
            <span className="overview-value">{fmtN(overviewData.supporters)}</span>
          </li>
          <li className="overview-row">
            <span className="overview-icon">{png('/icons/17.png', 'Donations')}</span>
            <span className="overview-label">All Donations</span>
            <span className="overview-value">{fmt$(overviewData.allDonations)}</span>
          </li>
        </ul>
      </div>

      {/* Key Actions */}
      <div className="action-card key-actions-card key-actions--simple">
        <h3 className="card-title">Key Actions</h3>
        <ul className="ka-list">
          {keyActions.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={a.onClick}
                className={`ka-item ${a.color === 'link' ? 'is-link' : ''}`}
              >
                <span className={`ka-dot ${a.color ? `ka-dot--${a.color}` : 'ka-dot--orange'}`} />
                <span className="ka-text">{a.title}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Fundraiser Progress (scrollbar visible) */}
      <div className="action-card fp-card scroll-card">
        <h3 className="card-title">Fundraiser Progress</h3>
        <div className="card-content fp-content">
          {!campaigns || campaigns.length === 0 ? (
            <div>
              <p>Currently you do not have any campaigns running.</p>
              <div style={{marginTop:'20px'}}>
                <button className="create-fundraiser-button" onClick={goToProfile}>Create One</button>
              </div>
            </div>
          ) : (
            campaigns.map((camp: any, idx: number) => {
              let progressPercent = Math.round(
                (parseInt(camp.progress) / parseInt(camp.goal)) * 100
              );

              return (
                <div key={camp.id} className="fp-item">
                  <div
                    className="fp-title"
                    onClick={() => {
                      history.push(camp?.slug ? `/donation/${camp.slug}` : `/donation/${reduxOrg._id}/${camp?.id}`);
                    }}
                  >
                    {camp.name.length <= 39
                      ? camp.name
                      : camp.name.substring(0, 38) + "..."}
                  </div>

                  <div className="dashboard-progress-bar">
                    <ProgressBar now={progressPercent} max={100} min={0} />
                  </div>

                  <div className="fp-amount">
                    {`${fmt$(camp.progress)} of ${fmt$(camp.goal)} raised`}
                  </div>
                </div>
              );
            })
          )}

        </div>
      </div>
      <div className="action-card ssh-card scroll-card">
        <div className="ssh-header">
          <h3>Social Share Hub</h3>
          <span className="ssh-info" aria-label="info" data-tip data-for='ssh-info'>
            i
            <ReactTooltip id='ssh-info' type='dark' effect='solid'>
              <span className='ssh-text'>
                Share your profile, wishlist, store, and fundraisers on social media.
              </span>
            </ReactTooltip>
          </span>
        </div>

        <div className="card-content ssh-content">
          <div className="ssh-tiles">
            {socialShareItems.filter(i => (i.variant ?? 'tile') === 'tile').map((item) => (
              <div
                key={item.id}
                onClick={() => { setShowSharingModal(true); setSelectedSharingItem(item.id); }}
                style={{ cursor: "pointer" }}
                className={`ssh-tile  ${item.accent ? `ssh-${item.accent}` : 'ssh-orange'}`}
              >
                <span className="ssh-share-glyph" aria-hidden="true">
                  <img src='/icons/41.png' alt={'share'} style={{ width: "20px", padding: '5px' }} loading="lazy" />
                </span>
                <span className="ssh-iconbubble">
                  {item.icon}
                </span>
                <span className="ssh-tile-title">{item.title}</span>
              </div>
            ))}
          </div>

          <div className="ssh-fundlist">
            {socialShareItems.filter(i => i.variant === 'fundraiser').map((item) => (
              <div
                key={item.id}
                className={`ssh-fund ${item.accent ? `ssh-${item.accent}` : 'ssh-purple'}`}
                onClick={() => {
                  setShowSharingModal(true);
                  setSelectedSharingItem(item.id);
                  setSelectedselectedCampaign(item)
                }}

                style={{ cursor: "pointer" }}
              >
                <span className="ssh-fund-flag" aria-hidden="true">{png('/icons/18.png', 'heart')}</span>
                <span className="ssh-fund-meta">
                  <span className="ssh-fund-label">Fundraiser</span>
                  <span className="ssh-fund-title">{item.title}</span>
                </span>
                <span className="ssh-share-glyph" aria-hidden="true">
                  <img src='/icons/41.png' alt={'share'} style={{ width: "20px", padding: '5px' }} loading="lazy" />
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>

      <Modal centered show={showSharingModal} onHide={() => setShowSharingModal(false)} className="manage-share-popups">
        <div className="manage-wish-wrap sharing-modal">
          <button className="sharing-close-x" onClick={() => setShowSharingModal(false)}>×</button>

          <div className="sharing-options-modal-body">
            <h4 className="sharing-options-modal-title">
              {selectedSharingItem === 'profile'
                ? 'Share your profile page'
                : selectedSharingItem === 'wishlist'
                  ? 'Share your wishlist'
                  : selectedSharingItem === 'fundraiser'
                    ? 'Share this Fundraiser'
                    : 'Share your marketplace'}
            </h4>

            {/* Circular icon row */}
            <div className="sharing-grid">
              <div className="share-item">
                <FacebookShareButton className="share-circle"
                  url={getSharingProps(selectedSharingItem!)?.url}
                  quote={getSharingProps(selectedSharingItem!)?.postDescription} hashtag="NEDDIE">
                  <FacebookIcon size={40} round />
                </FacebookShareButton>
                <small>Facebook</small>
              </div>

              <div className="share-item">
                <LinkedinShareButton className="share-circle" url={getSharingProps(selectedSharingItem!)?.url} summary={getSharingProps(selectedSharingItem!)?.postDescription}>
                  <LinkedinIcon size={40} round />
                </LinkedinShareButton>
                <small>LinkedIn</small>
              </div>

              <div className="share-item">
                <TwitterShareButton className="share-circle" url={getSharingProps(selectedSharingItem!)?.url} title={getSharingProps(selectedSharingItem!)?.postDescription} hashtags={getSharingProps(selectedSharingItem!)?.twitterHashtags}>
                  <img src={twitterX} alt="X" width={33.33} height={33.33} style={{ borderRadius: '50%' , minWidth: '33.33px' }} />
                </TwitterShareButton>
                <small>X</small>
              </div>

              <div className="share-item">
                <EmailShareButton className="share-circle" url={getSharingProps(selectedSharingItem!)?.url} subject={getSharingProps(selectedSharingItem!)?.postDescription}>
                  <EmailIcon size={40} round />
                </EmailShareButton>
                <small>Email</small>
              </div>

              <div className="share-item">
                <WhatsappShareButton className="share-circle" url={getSharingProps(selectedSharingItem!)?.url} title={getSharingProps(selectedSharingItem!)?.postDescription}>
                  <WhatsappIcon size={40} round />
                </WhatsappShareButton>
                <small>Whatsapp</small>
              </div>

              {/* <div className="share-item">
          <WhatsappShareButton className="share-circle" url={getSharingProps(selectedSharingItem!)?.url} title={getSharingProps(selectedSharingItem!)?.postDescription} separator=" — ">
            <WhatsappIcon size={40} round />
          </WhatsappShareButton>
          <small>Whatsapp</small>
        </div> */}
            </div>

            {/* Page link + copy */}
            <div className="share-link-row">
              <label>Page Link</label>
              <div className="share-link-input">
                <input
                  readOnly
                  value={getSharingProps(selectedSharingItem!)?.url || ''}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  type="button"
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(getSharingProps(selectedSharingItem!)?.url || '');
                    setCopy(true)
                  }}
                >
                  {copy ? <FaCheck /> : <FaRegCopy />}
                </button>
              </div>
            </div>

            <div className="close-button-container">
              <button className="btn btn-light" onClick={() => setShowSharingModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      </Modal>

    </div>
  );
};
export default ActionSection;
